import { formatAddress, type Address } from "./addressUtils";

/** Lo mínimo que hace falta de un local para imprimirlo en un ticket. */
export type TicketWarehouse = {
  id: string;
  name?: string | null;
  address?: Address | null;
};

/**
 * `id → local`, para resolver dónde retira el cliente.
 *
 * El nombre del local de retiro ya viaja en la línea, pero **la dirección no**:
 * ni el carrito ni el detalle de la orden la traen. Sin ella el ticket manda al
 * cliente a "Sucursal Norte" sin decirle dónde queda, que es la mitad del dato.
 */
export function indexWarehouses(
  items: any[] | undefined | null
): Record<string, TicketWarehouse> {
  return Object.fromEntries(
    (items ?? [])
      .filter((warehouse: any) => warehouse?.id)
      .map((warehouse: any) => [
        warehouse.id,
        {
          id: warehouse.id,
          name: warehouse.name,
          address: warehouse.address,
        },
      ])
  );
}

/**
 * Dónde retira el cliente ESTA línea: nombre y dirección del local.
 *
 * Devuelve `{}` cuando la línea se la lleva puesta del mostrador — el ticket
 * usa esa ausencia para decidir si imprime el bloque de retiro, así que una
 * línea normal no puede devolver campos vacíos "por las dudas".
 *
 * Acepta las dos formas que existen en la app porque el ticket se arma desde
 * las dos: la línea del **carrito** (`pickupWarehouseName`, puesta por el POS
 * al agregarla) y la línea de la **orden ya creada** (`warehouseName`, que
 * proyecta el BFF). Unificarlas en el origen sería mejor, pero son dos
 * contratos distintos y este helper es el punto donde se juntan.
 *
 * La dirección puede no existir: hay locales sin dirección cargada. En ese caso
 * sale sólo el nombre, nunca un renglón vacío ni "sin dirección".
 */
export function pickupPlaceFor(
  line: {
    fulfillmentStatus?: string | null;
    warehouseId?: string | null;
    pickupWarehouseName?: string | null;
    warehouseName?: string | null;
  },
  warehousesById: Record<string, TicketWarehouse>
): { pickupWarehouseName?: string; pickupWarehouseAddress?: string } {
  if (line?.fulfillmentStatus !== "pending_pickup") return {};

  const warehouse = line.warehouseId
    ? warehousesById[line.warehouseId]
    : undefined;

  const name =
    line.pickupWarehouseName ?? line.warehouseName ?? warehouse?.name ?? "";

  const address = warehouse?.address
    ? formatAddress(warehouse.address as Address)
    : "";

  return {
    pickupWarehouseName: name || undefined,
    pickupWarehouseAddress: address || undefined,
  };
}
